/**
 * Public Directory Service
 * 
 * Handles public directory search, sitemap, and browsing operations
 * Uses PublicApiSingleton for public directory operations
 */

import { PublicApiSingleton } from '@/providers/base/PublicApiSingleton';
import { ResolverType } from '@/types/resolver';
import { AppContext, CacheIsolation } from '@/utils/contextCacheManager';
import { clientLogger } from '@/lib/client-logger';

export interface DirectoryItem {
  id: string;
  name: string;
  slug: string;
  businessName?: string;
  description?: string;
  logoUrl?: string;
  bannerUrl?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  phone?: string;
  website?: string;
  email?: string;
  category?: string;
  primaryCategory?: string;
  rating?: number;
  reviewCount?: number;
  isFeatured?: boolean;
  isPublished?: boolean;
  location?: {
    latitude?: number;
    longitude?: number;
  };
  distance?: number; // in km/miles
  productCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface SitemapEntry {
  url: string;
  lastmod: string;
  changefreq: 'always' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'never';
  priority: number;
}

export interface LastViewedItem {
  id: string;
  name: string;
  slug: string;
  logoUrl?: string;
  category?: string;
  viewedAt: string;
  tenantId: string;
}

class PublicDirectoryService extends PublicApiSingleton {
  async resolveBySlug(slug: string): Promise<any> {
    // console.log('[PublicDirectoryService] Resolving slug:', slug);
    const response = await this.resolveIdentifier(slug, AppContext.TENANT);
    // console.log('[PublicDirectoryService] Resolved response:', response);
    return response;
  }

    protected defaultContext: AppContext = AppContext.STORE;
    protected defaultIsolation: CacheIsolation = CacheIsolation.STORE;
  private static instance: PublicDirectoryService;

  private constructor() {
    super('public-directory-service', {
      ttl: 15 * 60 * 1000 // 15 minutes for directory data
    });
  }

  public static getInstance(): PublicDirectoryService {
    if (!PublicDirectoryService.instance) {
      PublicDirectoryService.instance = new PublicDirectoryService();
    }
    return PublicDirectoryService.instance;
  }

  /**
   * Get directory item by slug or ID
   */
  async getDirectoryItem(identifier: string): Promise<DirectoryItem | null> {
    try {
      const result = await this.makeDefaultRequest<DirectoryItem>(
        `/api/directory/item/${encodeURIComponent(identifier)}`,
        {},
        `directory-item-${identifier}`
      );
      
      return result.data || null;
    } catch (error) {
      clientLogger.error('[PublicDirectoryService] Failed to get directory item:', { detail: error });
      return null;
    }
  }
 /**
  * Get shop info by slug or identifier (basic resolve)
  */
 async getShopInfo(tenantId: string): Promise<any | null> {
   try {
     const result = await this.makeDefaultRequest<any>(
       `/api/public/shops/${encodeURIComponent(tenantId)}`,
       {},
       `shop-info-${tenantId}`
     );
     if (!result.success){
      console.log('[PublicDirectoryService] Failed to get shop info:', result.error);
      return null;
     }
     return result.data || null;
   } catch (error) {
     clientLogger.error('[PublicDirectoryService] Failed to get shop info:', { detail: error });
     return null;
   }
 }

 /**
  * Get full shop details by tenant ID
  * Uses the /api/public/shops/id/:tenantId endpoint for complete shop data
  */
 async getShopInfoById(tenantId: string): Promise<any | null> {
   try {
     const result = await this.makeDefaultRequest<any>(
       `/api/public/shops/id/${encodeURIComponent(tenantId)}`,
       {},
       `shop-info-by-id-${tenantId}`
     );
    
    // console.log('[PublicDirectoryService] getShopInfoById result:', {
    //   success: result.success,
    //   hasData: !!result.data,
    //   dataKeys: result.data ? Object.keys(result.data) : 'null',
    //   fullData: JSON.stringify(result.data, null, 2)
    // });
    
    if (!result.success){
      console.log('[PublicDirectoryService] Failed to get shop info by ID:', result.error);
      return null;
    }
    return result.data || null;
  } catch (error) {
    clientLogger.error('[PublicDirectoryService] Failed to get shop info by ID:', { detail: error });
    return null;
  }
}

  /**
   * Get featured listings
   */
  async getFeaturedListings(limit: number = 10, category?: string): Promise<DirectoryItem[]> {
    try {
      const queryParams = new URLSearchParams({ limit: limit.toString() });
      if (category) {
        queryParams.append('category', category);
      }
      
      const result = await this.makeDefaultRequest<{ items: DirectoryItem[] }>(
        `/api/directory/featured?${queryParams.toString()}`,
        {},
        `directory-featured-${category || 'all'}-${limit}`,
        20 * 60 * 1000 // 20 minutes cache for featured items
      );
      
      return result.data?.items || [];
    } catch (error) {
      clientLogger.error('[PublicDirectoryService] Failed to get featured listings:', { detail: error });
      return [];
    }
  }

  /**
   * Get nearby listings
   */
  async getNearbyListings(latitude: number, longitude: number, radius: number = 10, limit: number = 20): Promise<DirectoryItem[]> {
    try {
      const queryParams = new URLSearchParams({
        latitude: latitude.toString(),
        longitude: longitude.toString(),
        radius: radius.toString(),
        limit: limit.toString()
      });
      
      const result = await this.makeDefaultRequest<{ items: DirectoryItem[] }>(
        `/api/directory/nearby?${queryParams.toString()}`,
        {},
        `directory-nearby-${latitude}-${longitude}-${radius}`,
        5 * 60 * 1000 // 5 minutes cache for location-based results
      );
      
      return result.data?.items || [];
    } catch (error) {
      clientLogger.error('[PublicDirectoryService] Failed to get nearby listings:', { detail: error });
      return [];
    }
  }

  /**
   * Get directory categories
   */
  async getCategories(): Promise<Array<{ name: string; count: number; slug?: string }>> {
    try {
      const result = await this.makeDefaultRequest<{ categories: Array<{ name: string; count: number; slug?: string }> }>(
        '/api/directory/categories',
        {},
        'directory-categories',
        30 * 60 * 1000 // 30 minutes cache for categories
      );
      if (!result.success){
        console.log('[PublicDirectoryService] Failed to get categories:', result.error);
        return [];
      }
      
      return result.data?.categories || [];
    } catch (error) {
      clientLogger.error('[PublicDirectoryService] Failed to get categories:', { detail: error });
      return [];
    }
  }

  /**
   * Get directory locations/cities
   */
  async getLocations(): Promise<Array<{ name: string; count: number; slug?: string }>> {
    try {
      const result = await this.makeDefaultRequest<{ locations: Array<{ name: string; count: number; slug?: string }> }>(
        '/api/directory/locations',
        {},
        'directory-locations',
        30 * 60 * 1000 // 30 minutes cache for locations
      );
      if (!result.success){
        console.log('[PublicDirectoryService] Failed to get locations:', result.error);
        return [];
      }
      
      return result.data?.locations || [];
    } catch (error) {
      clientLogger.error('[PublicDirectoryService] Failed to get locations:', { detail: error });
      return [];
    }
  }

  /**
   * Generate sitemap data
   */
  async getSitemap(): Promise<SitemapEntry[]> {
    try {
      const result = await this.makeDefaultRequest<{ entries: SitemapEntry[] }>(
        '/api/directory/sitemap',
        {},
        'directory-sitemap',
        60 * 60 * 1000 // 1 hour cache for sitemap
      );
      if (!result.success){
        console.log('[PublicDirectoryService] Failed to get sitemap:', result.error);
        return [];
      }
      
      return result.data?.entries || [];
    } catch (error) {
      clientLogger.error('[PublicDirectoryService] Failed to get sitemap:', { detail: error });
      return [];
    }
  }

  /**
   * Get recently viewed items for a user
   */
  async getLastViewed(limit: number = 10): Promise<LastViewedItem[]> {
    try {
      const result = await this.makeDefaultRequest<{ items: LastViewedItem[] }>(
        `/api/directory/recent?limit=${limit}`,
        {},
        'directory-recent-viewed',
        5 * 60 * 1000 // 5 minutes cache for recently viewed
      );
      if (!result.success){
        console.log('[PublicDirectoryService] Failed to get last viewed:', result.error);
        return [];
      }
      
      return result.data?.items || [];
    } catch (error) {
      clientLogger.error('[PublicDirectoryService] Failed to get last viewed:', { detail: error });
      return [];
    }
  }

  /**
   * Track a directory item view
   */
  async trackView(itemIdentifier: string, itemType: 'shop' | 'product' = 'shop'): Promise<void> {
    try {
      await this.makeDefaultRequest<void>(
        '/api/directory/track-view',
        {
          method: 'POST',
          body: JSON.stringify({
            identifier: itemIdentifier,
            itemType,
            timestamp: new Date().toISOString()
          })
        },
        `track-view-${itemIdentifier}`,
        0 // No caching for tracking
      );
    } catch (error) {
      clientLogger.error('[PublicDirectoryService] Failed to track view:', { detail: error });
      // Don't throw error for tracking failures
    }
  }

  /**
   * Get directory statistics
   */
  async getStatistics(): Promise<{
    totalShops: number;
    totalProducts: number;
    totalCategories: number;
    totalLocations: number;
    featuredShops: number;
    publishedShops: number;
  }> {
    try {
      const result = await this.makeDefaultRequest<any>(
        '/api/directory/stats',
        {},
        'directory-stats',
        15 * 60 * 1000 // 15 minutes cache for stats
      );
      if (!result.success){
        console.log('[PublicDirectoryService] Failed to get statistics:', result.error);
        return {
          totalShops: 0,
          totalProducts: 0,
          totalCategories: 0,
          totalLocations: 0,
          featuredShops: 0,
          publishedShops: 0
        };
      }
      
      return result.data || {
        totalShops: 0,
        totalProducts: 0,
        totalCategories: 0,
        totalLocations: 0,
        featuredShops: 0,
        publishedShops: 0
      };
    } catch (error) {
      clientLogger.error('[PublicDirectoryService] Failed to get statistics:', { detail: error });
      return {
        totalShops: 0,
        totalProducts: 0,
        totalCategories: 0,
        totalLocations: 0,
        featuredShops: 0,
        publishedShops: 0
      };
    }
  }

}

// Export the singleton instance
export const publicDirectoryService = PublicDirectoryService.getInstance();
